import { faker } from '@faker-js/faker';
import { generateFakeContacts } from './contactsData';

// Collection of venue types for random selection
const venueTypes = [
    'Concert Hall',
    'Exhibition Center',
    'Stadium',
    'Theater',
    'Convention Center',
    'Hotel Ballroom',
    'Outdoor Venue',
    'Arena',
    'Auditorium',
    'Gallery',
    'Museum',
    'Conference Center'
];

// Collection of placeholder images for venues
const venueImages = [
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Concert+Hall',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Exhibition+Center',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Stadium',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Theater',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Convention+Center',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Hotel+Venue',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Outdoor+Venue',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Arena',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Auditorium',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Gallery',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Museum',
    'https://placehold.co/600x400/e9e9e9/9b9b9b?text=Conference+Center'
];

/**
 * Generate a random phone number
 * @returns {String} Formatted phone number
 */
const generatePhoneNumber = () => {
    return faker.phone.number('+44 #### ### ###');
};

/**
 * Generate a fake address
 * @returns {Object} Address object with address1, address2, town, county, postcode, country
 */
const generateAddress = () => {
    return {
        address1: faker.location.streetAddress(),
        address2: faker.helpers.maybe(() => faker.location.secondaryAddress(), { probability: 0.7 }),
        town: faker.location.city(),
        county: faker.helpers.arrayElement([
            'Greater London', 'West Midlands', 'Greater Manchester',
            'West Yorkshire', 'Kent', 'Essex', 'Merseyside'
        ]),
        postcode: faker.location.zipCode('?# #??'),
        country: 'United Kingdom'
    };
};

/**
 * Generate a list of fake documents
 * @param {Number} count - Number of documents to generate
 * @returns {Array} Array of document objects
 */
const generateDocuments = (count = 3) => {
    const docTypes = ['Floor Plan', 'Safety Certificate', 'Insurance Document', 'Technical Rider', 'Contract', 'Invoice'];
    const documents = [];

    for (let i = 0; i < count; i++) {
        documents.push({
            id: faker.string.uuid(),
            name: faker.helpers.arrayElement(docTypes) + ' - ' + faker.date.recent().toLocaleDateString(),
            uploadDate: faker.date.recent().toISOString(),
            fileSize: faker.number.int({ min: 100, max: 5000 }) + 'KB',
            fileType: faker.helpers.arrayElement(['PDF', 'DOCX', 'JPG', 'PNG']),
            downloadUrl: '#'
        });
    }

    return documents;
};

/**
 * Create a fake venue
 * @param {String|Number} [id] - Optional ID to assign to the venue
 * @returns {Object} Venue object with fake data
 */
export const createFakeVenue = (id) => {
    const venueId = id || faker.string.uuid();
    const venueName = faker.company.name() + ' ' + faker.helpers.arrayElement(venueTypes);
    const venueType = faker.helpers.arrayElement(venueTypes);

    const staffCount = faker.number.int({ min: 1, max: 5 });
    const staff = generateFakeContacts(staffCount).map(contact => {
        // Override some properties to make it relevant to the venue
        return {
            ...contact,
            jobTitle: faker.helpers.arrayElement([
                'Venue Manager', 'Technical Director', 'Production Manager',
                'Front of House Manager', 'Stage Manager', 'Box Office Manager'
            ]),
            company: venueName
        };
    });

    return {
        id: venueId,
        name: venueName,
        type: venueType,
        phone: generatePhoneNumber(),
        email: faker.internet.email({ firstName: venueName.split(' ')[0], lastName: 'venue', provider: 'example.com' }).toLowerCase(),
        image: faker.helpers.arrayElement(venueImages),
        capacity: faker.number.int({ min: 50, max: 5000 }),
        description: faker.lorem.paragraph(),
        website: faker.internet.url(),
        address: generateAddress(),
        staff: staff,
        documents: generateDocuments(faker.number.int({ min: 2, max: 6 })),
        bookable: faker.datatype.boolean(),
        createdAt: faker.date.past(),
        updatedAt: faker.date.recent()
    };
};

/**
 * Generate a collection of fake venues
 * @param {Number} count - Number of venues to generate
 * @returns {Array} Array of venue objects
 */
export const generateFakeVenues = (count = 10) => {
    const venues = [];
    for (let i = 0; i < count; i++) {
        venues.push(createFakeVenue());
    }
    return venues;
}; 